import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useAllMaterials, useRecentViews } from 'hooks';
import { getHotNews } from 'modules/News/api';
import { getDashboardConfig, saveDashboardConfig } from 'services/dashboardService';
import './dashboard.css';

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const ResizeHandleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M14 14L14 10M14 14L10 14M14 14C12 12 11 11 9 9"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 8L14 6M14 8L12 8M14 8C13 7 12.5 6.5 11 5"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
  </svg>
);

const IconCaretUp = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="currentColor">
    <path d="M4.5 1.5L8 7.5H1L4.5 1.5Z"/>
  </svg>
);

const IconCaretDown = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="currentColor">
    <path d="M4.5 7.5L1 1.5H8L4.5 7.5Z"/>
  </svg>
);

const IconLineChart = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 3 9 21 6 12 2 12"/>
  </svg>
);

const IconNewspaper = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2"/>
    <path d="M7 8h10M7 12h10M7 16h6"/>
  </svg>
);

const IconBolt = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const IconBell = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

const IconBarChart = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="12" width="4" height="9"/>
    <rect x="10" y="7" width="4" height="14"/>
    <rect x="17" y="3" width="4" height="18"/>
  </svg>
);

const IconClock = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6.5"/>
    <path d="M8 4.5V8L10.5 10"/>
  </svg>
);

const IconWarning = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 1.5L15 14H1L8 1.5Z"/>
    <path d="M8 6V9.5M8 11.5V12.5"/>
  </svg>
);

const IconPackage = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 5L8 2L14 5V11L8 14L2 11V5Z"/>
    <path d="M8 2V14M2 5L8 8L14 5"/>
  </svg>
);

const IconTrendDown = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4L6 9L9 6L14 12"/>
    <path d="M10.5 12H14V8.5"/>
  </svg>
);

const IconX = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M1 1L10 10M10 1L1 10"/>
  </svg>
);

const IconCheck = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 6L4.5 9.5L10.5 2"/>
  </svg>
);

const IconPencil = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11.5 2.5L13.5 4.5L5.5 13.5H2.5V10.5L11.5 2.5Z"/>
    <path d="M9.5 4.5L11.5 6.5"/>
  </svg>
);

const IconStar = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 1.5L9.76 6.02L14.5 6.27L11.02 9.15L12.16 13.77L8 11.32L3.84 13.77L4.98 9.15L1.5 6.27L6.24 6.02L8 1.5Z"/>
  </svg>
);

const IconGridEmpty = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="18" height="18" rx="3"/>
    <rect x="26" y="4" width="18" height="18" rx="3"/>
    <rect x="4" y="26" width="18" height="18" rx="3"/>
    <rect x="26" y="26" width="18" height="18" rx="3"/>
  </svg>
);

const IconArrowLeft = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 7H3M3 7L7 3M3 7L7 11"/>
  </svg>
);

// ─── Grid constants ───────────────────────────────────────────────────────────

const NUM_COLS = 6;
const ROW_H = 155; // px, must match grid-auto-rows in CSS
const GAP = 14;    // px, must match gap in CSS

// Max colSpan / rowSpan per widget type
const WIDGET_SIZE_LIMITS = {
  'chart-material': { maxCols: 2, maxRows: 2 },
  'news-feed':      { maxCols: 2, maxRows: 4 },
  'early-signals':  { maxCols: 2, maxRows: 4 },
  'alerts':         { maxCols: 2, maxRows: 3 },
  'activity':       { maxCols: 2, maxRows: 2 },
};

// ─── Mock Early Signals data ───────────────────────────────────────────────────

const EARLY_SIGNALS = [
  {
    id: '1', title: 'Lithium prices surge 45% – massive EV sector demand',
    type: 'Market', impact: 'Critical', impactClass: 'impact-critical',
    typeClass: 'signal-type-market', timeframe: '7 days', region: 'Global', category: 'Price Movement',
  },
  {
    id: '2', title: 'Major port disruption in Rotterdam – 2–3 week delays expected',
    type: 'Supply Chain', impact: 'High', impactClass: 'impact-high',
    typeClass: 'signal-type-supply', timeframe: '14 days', region: 'EU', category: 'Logistics',
  },
  {
    id: '3', title: 'EU Carbon Border Adjustment Mechanism Phase 2',
    type: 'Regulatory', impact: 'High', impactClass: 'impact-high',
    typeClass: 'signal-type-regulatory', timeframe: '221 days', region: 'EU', category: 'Environmental',
  },
  {
    id: '4', title: 'China announces export restrictions on rare earths',
    type: 'Market', impact: 'Critical', impactClass: 'impact-critical',
    typeClass: 'signal-type-market', timeframe: '30 days', region: 'APAC', category: 'Trade Policy',
  },
  {
    id: '5', title: 'New copper mine in Chile delayed by 6 months',
    type: 'Supply Chain', impact: 'Medium', impactClass: 'impact-medium',
    typeClass: 'signal-type-supply', timeframe: '180 days', region: 'LATAM', category: 'Production',
  },
  {
    id: '6', title: 'Steel futures show unusual volatility – possible trend shift',
    type: 'Price', impact: 'Medium', impactClass: 'impact-medium',
    typeClass: 'signal-type-price', timeframe: '3 days', region: 'EU & US', category: 'Price Movement',
  },
];

// ─── Widget type definitions ───────────────────────────────────────────────────

const WIDGET_TYPES = [
  { type: 'chart-material', icon: <IconLineChart size={28}/>, name: 'Price Chart', desc: 'Track material price over time' },
  { type: 'news-feed', icon: <IconNewspaper size={28}/>, name: 'News Feed', desc: 'Latest market news' },
  { type: 'early-signals', icon: <IconBolt size={28}/>, name: 'Early Signals', desc: 'Regulatory & market signals' },
  { type: 'alerts', icon: <IconBell size={28}/>, name: 'Price Alerts', desc: 'Your configured price alerts' },
  { type: 'activity', icon: <IconBarChart size={28}/>, name: 'Activity', desc: 'Platform activity stats' },
];

// ─── Default initial widgets ───────────────────────────────────────────────────

function buildDefaultWidgets(materials) {
  const items = materials || [];
  const widgets = [];
  const colors = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#3b82f6'];

  items.slice(0, 4).forEach((m, i) => {
    widgets.push({
      id: `chart-${m.code}`,
      type: 'chart-material',
      col: (i % 2) * 2,
      row: Math.floor(i / 2) * 2,
      colSpan: 2,
      rowSpan: 2,
      config: { materialCode: m.code, color: colors[i] },
    });
  });

  widgets.push({
    id: 'news-1',
    type: 'news-feed',
    col: 4,
    row: 0,
    colSpan: 2,
    rowSpan: 2,
    config: {},
  });

  widgets.push({
    id: 'signals-1',
    type: 'early-signals',
    col: 4,
    row: 2,
    colSpan: 2,
    rowSpan: 2,
    config: {},
  });

  return widgets;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function findEmptyPosition(colSpan, rowSpan, existingWidgets) {
  for (let row = 0; row < 100; row++) {
    for (let col = 0; col <= NUM_COLS - colSpan; col++) {
      const collides = existingWidgets.some(w => {
        const wEndCol = w.col + w.colSpan;
        const wEndRow = w.row + w.rowSpan;
        const tEndCol = col + colSpan;
        const tEndRow = row + rowSpan;
        return !(tEndCol <= w.col || col >= wEndCol || tEndRow <= w.row || row >= wEndRow);
      });
      if (!collides) return { col, row };
    }
  }
  return { col: 0, row: 0 };
}

function swapWidgets(widgets, draggedId, targetId) {
  const dragged = widgets.find(w => w.id === draggedId);
  const target = widgets.find(w => w.id === targetId);
  if (!dragged || !target) return widgets;
  return widgets.map(w => {
    if (w.id === draggedId) return { ...w, col: target.col, row: target.row, colSpan: target.colSpan, rowSpan: target.rowSpan };
    if (w.id === targetId) return { ...w, col: dragged.col, row: dragged.row, colSpan: dragged.colSpan, rowSpan: dragged.rowSpan };
    return w;
  });
}

// ─── Sparkline ───────────────────────────────────────────────────────────────

function Sparkline({ data, color = '#8b5cf6', up }) {
  const stroke = up === false ? '#f87171' : (up === true ? '#34d399' : color);
  const chartData = (data || []).map((v, i) => ({ i, value: v }));
  if (chartData.length < 2) return <div style={{ height: '100%' }} />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
        <defs>
          <linearGradient id={`spark-${stroke.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={stroke} stopOpacity={0.3} />
            <stop offset="95%" stopColor={stroke} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="value" stroke={stroke} strokeWidth={1.5}
          fill={`url(#spark-${stroke.replace('#','')})`} isAnimationActive={false} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── Chart widget with full area chart ───────────────────────────────────────

function ChartWidgetContent({ material, colSpan, rowSpan, color, onNavigate }) {
  if (!material) {
    return (
      <div className="db-widget-content" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>No data</span>
      </div>
    );
  }

  const chartData = material.recent_chart_data || material.time_series?.map((d, i) => ({ date: String(d.year || i), value: d.value })) || [];
  const formattedData = chartData.length > 0 ? chartData : (material.chartData || []).map((v, i) => ({ date: String(i), value: v }));
  const lastTwo = (material.recent_chart_data || []).slice(-2);
  const changePct = lastTwo.length === 2 && lastTwo[0]?.value && lastTwo[1]?.value
    ? ((lastTwo[1].value - lastTwo[0].value) / Math.abs(lastTwo[0].value)) * 100
    : null;
  const changeUp = changePct !== null ? changePct >= 0 : null;
  const col = color || '#8b5cf6';

  if (colSpan === 1 && rowSpan === 1) {
    return (
      <div className="db-widget-content">
        <div className="wg-chart-header">
          <span className="wg-chart-name" onClick={onNavigate}>{material.name}</span>
          {changeUp !== null && (
            <span className={changeUp ? 'wg-chart-change-up' : 'wg-chart-change-down'} style={{ marginLeft: 6 }}>
              {changeUp ? <IconCaretUp/> : <IconCaretDown/>} {Math.abs(changePct).toFixed(1)}%
            </span>
          )}
        </div>
        <div style={{ fontSize: 14, color: '#fff', marginBottom: 4 }}>{material.price || '—'}</div>
        <div className="wg-chart-area" style={{ minHeight: 60 }}>
          <FullChart data={formattedData} color={col} colSpan={1} rowSpan={1} id={material.code} />
        </div>
      </div>
    );
  }

  if (colSpan >= 2 && rowSpan === 1) {
    return (
      <div className="db-widget-content">
        <div className="wg-chart-header">
          <span className="wg-chart-name" onClick={onNavigate}>{material.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span className="wg-chart-price">{material.price || '—'}</span>
            {changeUp !== null && (
              <span className={changeUp ? 'wg-chart-change-up' : 'wg-chart-change-down'}>
                {changeUp ? <IconCaretUp/> : <IconCaretDown/>} {Math.abs(changePct).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
        <div className="wg-chart-area" style={{ flex: 1, marginTop: 4 }}>
          <FullChart data={formattedData} color={col} colSpan={2} rowSpan={1} id={material.code} />
        </div>
      </div>
    );
  }

  if (colSpan === 1 && rowSpan >= 2) {
    return (
      <div className="db-widget-content">
        <div style={{ marginBottom: 8, flexShrink: 0 }}>
          <span className="wg-chart-name" onClick={onNavigate}>{material.name}</span>
        </div>
        <div style={{ flexShrink: 0, marginBottom: 8 }}>
          <div className="wg-chart-price-lg">{material.price || '—'}</div>
          {changeUp !== null && (
            <span className={changeUp ? 'wg-chart-change-up' : 'wg-chart-change-down'}>
              {changeUp ? <IconCaretUp/> : <IconCaretDown/>} {Math.abs(changePct).toFixed(1)}%
            </span>
          )}
        </div>
        <div className="wg-chart-area" style={{ flex: 1 }}>
          <FullChart data={formattedData} color={col} colSpan={1} rowSpan={2} id={material.code} />
        </div>
      </div>
    );
  }

  return (
    <div className="db-widget-content">
      <div className="wg-chart-header" style={{ marginBottom: 12, flexShrink: 0 }}>
        <span className="wg-chart-name" style={{ fontSize: 12 }} onClick={onNavigate}>{material.name}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span className="wg-chart-price-xl">{material.price || '—'}</span>
          {changeUp !== null && (
            <span className={changeUp ? 'wg-chart-change-up' : 'wg-chart-change-down'} style={{ fontSize: 12 }}>
              {changeUp ? <IconCaretUp/> : <IconCaretDown/>} {Math.abs(changePct).toFixed(1)}%
            </span>
          )}
        </div>
      </div>
      <div className="wg-chart-area" style={{ flex: 1 }}>
        <FullChart data={formattedData} color={col} colSpan={colSpan} rowSpan={rowSpan} id={material.code} />
      </div>
    </div>
  );
}

function FullChart({ data, color, colSpan, rowSpan, id }) {
  const gradId = `cg-${(id || '').replace(/[^a-z0-9]/gi, '')}`;
  const showAxes = colSpan >= 2 || rowSpan >= 2;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: showAxes ? 0 : 4, left: showAxes ? -10 : -30, bottom: showAxes ? 0 : -10 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {showAxes && <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />}
        {showAxes && (
          <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.2)', fontSize: 9 }}
            tickLine={false} axisLine={false} interval="preserveStartEnd" />
        )}
        {showAxes && (
          <YAxis tick={{ fill: 'rgba(255,255,255,0.2)', fontSize: 9 }}
            tickLine={false} axisLine={false} width={32}
            tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : String(v)} />
        )}
        {showAxes && (
          <Tooltip
            contentStyle={{ background: 'rgba(0,0,0,0.85)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 10px' }}
            labelStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}
            itemStyle={{ color, fontSize: 12 }}
          />
        )}
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2}
          fill={`url(#${gradId})`} isAnimationActive={false} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── Main DashboardPage ───────────────────────────────────────────────────────

const DashboardPage = () => {
  const navigate = useNavigate();

  const { data: allMaterials, loading: materialsLoading } = useAllMaterials({ slim: true, cache: true, cacheTTL: 5 * 60 * 1000 });
  const { data: recentViews } = useRecentViews({ limit: 6, cache: true, cacheTTL: 2 * 60 * 1000 });
  const [hotNews, setHotNews] = useState([]);

  const [dashboards, setDashboards] = useState([{ id: 'dashboard-1', name: 'Dashboard 1', widgets: [] }]);
  const [activeDashboardId, setActiveDashboardId] = useState('dashboard-1');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [widgetsSnapshot, setWidgetsSnapshot] = useState(null);

  // Drag state
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragTarget, setDragTarget] = useState(null); // empty cell drop target

  const gridRef = useRef(null);

  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabName, setEditingTabName] = useState('');
  const editInputRef = useRef(null);

  const [deleteTabConfirm, setDeleteTabConfirm] = useState(null);

  const [showAddWidget, setShowAddWidget] = useState(false);
  const [addWidgetType, setAddWidgetType] = useState(null);
  const [matSearch, setMatSearch] = useState('');

  // ── Derived ────────────────────────────────────────────────────────────────

  const currentDashboard = dashboards.find(d => d.id === activeDashboardId);
  const widgets = currentDashboard?.widgets || [];

  const setWidgets = useCallback((updater) => {
    setDashboards(prev => prev.map(d => {
      if (d.id !== activeDashboardId) return d;
      const next = typeof updater === 'function' ? updater(d.widgets) : updater;
      return { ...d, widgets: next };
    }));
  }, [activeDashboardId]);

  const materialMap = React.useMemo(() => {
    const m = {};
    (allMaterials || []).forEach(mat => { m[mat.code] = mat; });
    return m;
  }, [allMaterials]);

  // Number of explicit grid rows shown in edit mode (widget area + 2 empty rows)
  const numRows = React.useMemo(() => {
    const maxRow = widgets.reduce((m, w) => Math.max(m, w.row + w.rowSpan), 0);
    return Math.max(3, maxRow + 2);
  }, [widgets]);

  // Which individual grid cells are occupied — excludes the dragged widget so its
  // current cells become valid drop targets (allows moving to overlapping positions)
  const occupiedCells = React.useMemo(() => {
    const s = new Set();
    widgets.forEach(w => {
      if (w.id === draggedId) return;
      for (let c = w.col; c < w.col + w.colSpan; c++)
        for (let r = w.row; r < w.row + w.rowSpan; r++)
          s.add(`${c},${r}`);
    });
    return s;
  }, [widgets, draggedId]);

  // Which cells should be highlighted as the drop preview for the dragged widget
  const highlightedCells = React.useMemo(() => {
    if (!dragTarget || !draggedId) return new Set();
    const dragged = widgets.find(w => w.id === draggedId);
    if (!dragged) return new Set();
    const s = new Set();
    for (let c = dragTarget.col; c < dragTarget.col + dragged.colSpan; c++)
      for (let r = dragTarget.row; r < dragTarget.row + dragged.rowSpan; r++)
        s.add(`${c},${r}`);
    return s;
  }, [dragTarget, draggedId, widgets]);

  // Whether the dragged widget can be placed at a given (col, row)
  const canPlaceAt = useCallback((col, row) => {
    if (!draggedId) return false;
    const dragged = widgets.find(w => w.id === draggedId);
    if (!dragged) return false;
    if (col + dragged.colSpan > NUM_COLS) return false;
    const others = widgets.filter(w => w.id !== dragged.id);
    return !others.some(w => !(
      col + dragged.colSpan <= w.col || col >= w.col + w.colSpan ||
      row + dragged.rowSpan <= w.row || row >= w.row + w.rowSpan
    ));
  }, [draggedId, widgets]);

  // ── Load config ───────────────────────────────────────────────────────────

  useEffect(() => {
    getDashboardConfig()
      .then(cfg => {
        if (cfg && cfg.dashboards?.length > 0) {
          setDashboards(cfg.dashboards);
          setActiveDashboardId(cfg.activeDashboardId || cfg.dashboards[0].id);
        }
        setConfigLoaded(true);
      })
      .catch(() => setConfigLoaded(true));
  }, []);

  // ── Initialize default widgets when materials first load ──────────────────

  useEffect(() => {
    if (!configLoaded || !allMaterials?.length) return;
    setDashboards(prev => {
      if (prev.some(d => d.widgets.length > 0)) return prev;
      const initial = buildDefaultWidgets(allMaterials);
      return prev.map((d, i) => i === 0 ? { ...d, widgets: initial } : d);
    });
  }, [configLoaded, allMaterials]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getHotNews({ limit: 10 }).then(data => {
      if (Array.isArray(data)) setHotNews(data);
      else if (data?.items) setHotNews(data.items);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (editingTabId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingTabId]);

  // ── Save config ───────────────────────────────────────────────────────────

  const doSave = useCallback((newDashboards, activeId) => {
    setSaving(true);
    saveDashboardConfig({ activeDashboardId: activeId, dashboards: newDashboards })
      .finally(() => setSaving(false));
  }, []);

  // ── Edit mode ─────────────────────────────────────────────────────────────

  const enterEditMode = () => {
    setWidgetsSnapshot(JSON.parse(JSON.stringify(widgets)));
    setIsEditMode(true);
  };

  const saveEditMode = () => {
    setWidgetsSnapshot(null);
    setIsEditMode(false);
    doSave(dashboards, activeDashboardId);
  };

  const cancelEditMode = () => {
    if (widgetsSnapshot) setWidgets(widgetsSnapshot);
    setWidgetsSnapshot(null);
    setIsEditMode(false);
  };

  // ── Dashboard tabs ────────────────────────────────────────────────────────

  const addDashboard = () => {
    const id = `dashboard-${Date.now()}`;
    const newDb = { id, name: `Dashboard ${dashboards.length + 1}`, widgets: [] };
    const next = [...dashboards, newDb];
    setDashboards(next);
    setActiveDashboardId(id);
  };

  const deleteTab = (id) => {
    if (dashboards.length <= 1) return;
    const next = dashboards.filter(d => d.id !== id);
    setDashboards(next);
    if (activeDashboardId === id) setActiveDashboardId(next[0].id);
    setDeleteTabConfirm(null);
  };

  const startRenameTab = (id, name) => {
    setEditingTabId(id);
    setEditingTabName(name);
  };

  const submitRenameTab = () => {
    if (editingTabId && editingTabName.trim()) {
      setDashboards(prev => prev.map(d => d.id === editingTabId ? { ...d, name: editingTabName.trim() } : d));
    }
    setEditingTabId(null);
    setEditingTabName('');
  };

  // ── Drag & Drop ───────────────────────────────────────────────────────────

  const onDragStart = (e, id) => {
    if (!isEditMode) return;
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
    setDragTarget(null);
  };

  // Drag over an existing widget (swap behavior)
  const onDragOver = (e, id) => {
    if (!isEditMode || !draggedId || id === draggedId) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(id);
    setDragTarget(null);
  };

  const onDrop = (e, targetId) => {
    if (!isEditMode || !draggedId || draggedId === targetId) return;
    e.preventDefault();
    e.stopPropagation();
    const did = draggedId;
    setWidgets(prev => swapWidgets(prev, did, targetId));
    setDraggedId(null);
    setDragOverId(null);
    setDragTarget(null);
  };

  // Compute grid cell (col, row) from a mouse/drag event using coordinate math.
  // This runs on the grid container itself so z-index of child elements doesn't matter.
  const getCellFromEvent = useCallback((e) => {
    const grid = gridRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const cellW = (rect.width - (NUM_COLS - 1) * GAP) / NUM_COLS;
    const col = Math.floor((e.clientX - rect.left) / (cellW + GAP));
    const row = Math.floor((e.clientY - rect.top) / (ROW_H + GAP));
    if (col < 0 || col >= NUM_COLS || row < 0) return null;
    return { col, row };
  }, []);

  // Grid-level dragOver: handles both empty-space drops AND dragging over own cells.
  // Per-widget onDragOver calls stopPropagation for OTHER widgets (swap path),
  // so this only fires when hovering over empty space or the dragged widget itself.
  const onGridDragOver = useCallback((e) => {
    if (!isEditMode || !draggedId) return;
    const cell = getCellFromEvent(e);
    if (!cell) return;
    if (canPlaceAt(cell.col, cell.row)) {
      e.preventDefault();
      setDragTarget({ col: cell.col, row: cell.row });
      setDragOverId(null);
    } else {
      setDragTarget(null);
    }
  }, [isEditMode, draggedId, getCellFromEvent, canPlaceAt]);

  const onGridDrop = useCallback((e) => {
    if (!isEditMode || !draggedId) return;
    const cell = getCellFromEvent(e);
    if (!cell || !canPlaceAt(cell.col, cell.row)) return;
    e.preventDefault();
    const did = draggedId;
    setWidgets(prev => prev.map(w => w.id === did ? { ...w, col: cell.col, row: cell.row } : w));
    setDraggedId(null);
    setDragOverId(null);
    setDragTarget(null);
  }, [isEditMode, draggedId, getCellFromEvent, canPlaceAt, setWidgets]);

  // ── Resize (drag handle) ──────────────────────────────────────────────────

  const onResizeMouseDown = useCallback((e, widgetId) => {
    e.preventDefault();
    e.stopPropagation();
    const widget = widgets.find(w => w.id === widgetId);
    const grid = gridRef.current;
    if (!grid || !widget) return;

    const gridRect = grid.getBoundingClientRect();
    // Cell width = (total width minus all gaps) / num cols, then add one gap for full step size
    const cellW = (gridRect.width - (NUM_COLS - 1) * GAP) / NUM_COLS + GAP;
    const cellH = ROW_H + GAP;

    const startX = e.clientX;
    const startY = e.clientY;
    const startColSpan = widget.colSpan;
    const startRowSpan = widget.rowSpan;
    const startCol = widget.col;

    const limits = WIDGET_SIZE_LIMITS[widget.type] || { maxCols: NUM_COLS, maxRows: 8 };

    const moveHandler = (ev) => {
      const colDelta = Math.round((ev.clientX - startX) / cellW);
      const rowDelta = Math.round((ev.clientY - startY) / cellH);
      const newColSpan = Math.max(1, Math.min(Math.min(limits.maxCols, NUM_COLS - startCol), startColSpan + colDelta));
      const newRowSpan = Math.max(1, Math.min(limits.maxRows, startRowSpan + rowDelta));
      setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, colSpan: newColSpan, rowSpan: newRowSpan } : w));
    };

    const upHandler = () => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  }, [widgets, setWidgets]);

  // ── Widget delete ─────────────────────────────────────────────────────────

  const deleteWidget = (e, id) => {
    e.stopPropagation();
    setWidgets(prev => prev.filter(w => w.id !== id));
  };

  // ── Add Widget ────────────────────────────────────────────────────────────

  const openAddWidget = () => {
    setAddWidgetType(null);
    setMatSearch('');
    setShowAddWidget(true);
  };

  const addWidget = (type, cfg = {}) => {
    const defaultSizes = {
      'chart-material': { colSpan: 2, rowSpan: 2 },
      'news-feed':      { colSpan: 2, rowSpan: 2 },
      'early-signals':  { colSpan: 2, rowSpan: 2 },
      'alerts':         { colSpan: 1, rowSpan: 2 },
      'activity':       { colSpan: 1, rowSpan: 2 },
    };
    const { colSpan, rowSpan } = defaultSizes[type] || { colSpan: 2, rowSpan: 2 };
    const pos = findEmptyPosition(colSpan, rowSpan, widgets);
    const id = `${type}-${Date.now()}`;
    setWidgets(prev => [...prev, { id, type, ...pos, colSpan, rowSpan, config: cfg }]);
    setShowAddWidget(false);
  };

  // ── Render widget content ─────────────────────────────────────────────────

  const renderWidget = (widget) => {
    const { type, colSpan, rowSpan, config } = widget;

    if (type === 'chart-material') {
      const material = materialMap[config?.materialCode];
      return (
        <ChartWidgetContent
          material={material}
          colSpan={colSpan}
          rowSpan={rowSpan}
          color={config?.color || '#8b5cf6'}
          onNavigate={() => material && navigate(`/charts/material/${material.code}`)}
        />
      );
    }

    if (type === 'news-feed') {
      // Items to show based on widget height
      const itemCount = rowSpan <= 1 ? 2 : rowSpan === 2 ? 5 : rowSpan === 3 ? 8 : 12;
      const items = hotNews.slice(0, itemCount);
      const showImages = colSpan >= 2 || rowSpan >= 2;
      return (
        <div className="db-widget-content">
          <div className="wg-news-header">
            <span className="wg-news-title"><IconNewspaper size={14}/> News Feed</span>
            <button className="db-btn" style={{ padding: '4px 8px', fontSize: 11 }}
              onClick={() => navigate('/news')}>All news</button>
          </div>
          <div className="wg-news-list">
            {items.length === 0 && (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, padding: '20px 0', textAlign: 'center' }}>
                Loading news…
              </div>
            )}
            {items.map((item, idx) => (
              <div key={item.id || idx} className="wg-news-item"
                onClick={() => navigate(item.id ? `/news/news-view/${item.id}` : '/news')}>
                {showImages && item.image_url && (
                  <img className="wg-news-img" src={item.image_url} alt=""
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
                <div className="wg-news-body">
                  <div className="wg-news-item-title">{item.title}</div>
                  <div className="wg-news-meta">
                    {item.recent_at ? timeAgo(item.recent_at) : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (type === 'early-signals') {
      const count = (colSpan === 1 && rowSpan === 1) ? 1 : (colSpan + rowSpan <= 3 ? 2 : 6);
      return (
        <div className="db-widget-content">
          <div className="wg-signal-header">
            <span className="wg-signal-title"><IconBolt size={14}/> Early Signals</span>
            <button className="db-btn" style={{ padding: '4px 8px', fontSize: 11 }}>All</button>
          </div>
          <div className="wg-signal-list">
            {EARLY_SIGNALS.slice(0, count).map(sig => (
              <div key={sig.id} className="wg-signal-item">
                <div className="wg-signal-row">
                  <div className="wg-signal-badges">
                    <div className={`wg-signal-type ${sig.typeClass}`}>{sig.type[0]}</div>
                    <span className={`wg-signal-impact ${sig.impactClass}`}>{sig.impact}</span>
                  </div>
                  <span className="wg-signal-region">{sig.region}</span>
                </div>
                <div className="wg-signal-text">{sig.title}</div>
                <div className="wg-signal-footer">
                  <span className="wg-signal-time"><IconClock size={11}/> {sig.timeframe}</span>
                  <span className="wg-signal-category">{sig.category}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (type === 'alerts') {
      return (
        <div className="db-widget-content">
          <div className="wg-alerts-title"><IconBell size={14}/> Price Alerts</div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {[
              { name: 'Lithium Carbonate', desc: 'Price exceeds €75,000 threshold', time: '10 min ago', color: 'orange', icon: <IconWarning/> },
              { name: 'Cobalt Sulfate', desc: 'New supplier available in your region', time: '1h ago', color: 'blue', icon: <IconPackage/> },
              { name: 'Nickel', desc: 'Significant price drop detected', time: '3h ago', color: 'red', icon: <IconTrendDown/> },
            ].map((alert, i) => (
              <div key={i} className={`wg-alert-item ${alert.color}`}>
                <div className={`wg-alert-icon ${alert.color}`}>{alert.icon}</div>
                <div className="wg-alert-body">
                  <div className="wg-alert-name">{alert.name}</div>
                  <div className="wg-alert-desc">{alert.desc}</div>
                  <div className="wg-alert-time">{alert.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (type === 'activity') {
      return (
        <div className="db-widget-content">
          <div className="wg-activity-title">Activity</div>
          <div className="wg-stat">
            <div className="wg-stat-label">Transactions</div>
            <div className="wg-stat-value">1,247</div>
            <div className="wg-stat-change-up">+18% today</div>
          </div>
          <div className="wg-stat">
            <div className="wg-stat-label">Avg. Deal Size</div>
            <div className="wg-stat-value">€1,925</div>
            <div className="wg-stat-change-neutral">+3%</div>
          </div>
        </div>
      );
    }

    return null;
  };

  // ── Favorites bar ──────────────────────────────────────────────────────────

  const favMaterials = recentViews?.slice(0, 6) || [];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="db-page">
      <div>

        {/* Header */}
        <div className="db-header">
          <div className="db-tabs">
            {dashboards.map(d => (
              <div
                key={d.id}
                className={`db-tab ${d.id === activeDashboardId ? 'active' : ''}`}
                onClick={() => !editingTabId && setActiveDashboardId(d.id)}
                onDoubleClick={() => isEditMode && startRenameTab(d.id, d.name)}
              >
                {editingTabId === d.id ? (
                  <input
                    ref={editInputRef}
                    className="db-tab-input"
                    value={editingTabName}
                    onChange={e => setEditingTabName(e.target.value)}
                    onBlur={submitRenameTab}
                    onKeyDown={e => {
                      if (e.key === 'Enter') submitRenameTab();
                      if (e.key === 'Escape') { setEditingTabId(null); setEditingTabName(''); }
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span>{d.name}</span>
                )}
                {isEditMode && dashboards.length > 1 && editingTabId !== d.id && (
                  <button className="db-tab-delete" onClick={e => { e.stopPropagation(); setDeleteTabConfirm(d.id); }}>
                    <IconX size={9}/>
                  </button>
                )}
              </div>
            ))}
            {isEditMode && (
              <button className="db-tab-add" onClick={addDashboard} title="New dashboard">+</button>
            )}
            {isEditMode && <span className="db-edit-badge">EDIT MODE</span>}
          </div>

          <div className="db-header-actions">
            {isEditMode ? (
              <>
                <button className="db-btn" onClick={openAddWidget}>+ Add Widget</button>
                <button className="db-btn" onClick={cancelEditMode}><IconX size={10}/> Cancel</button>
                <button className="db-btn db-btn-primary" onClick={saveEditMode}>
                  {saving ? 'Saving…' : <><IconCheck size={11}/> Done</>}
                </button>
              </>
            ) : (
              <button className="db-btn" onClick={enterEditMode}><IconPencil/> Edit Dashboard</button>
            )}
          </div>
        </div>

        {/* Favorites Bar */}
        <div className="db-favorites">
          <div className="db-favorites-header">
            <IconStar/> Recently Viewed
          </div>
          {favMaterials.length === 0 ? (
            <div className="db-fav-empty">
              View some materials in Charts to see them here
            </div>
          ) : (
            <div className="db-favorites-grid">
              {favMaterials.map((mat, i) => {
                const values = mat.chartData || [];
                const first = values[0];
                const last = values[values.length - 1];
                const up = values.length > 1 ? last >= first : null;
                return (
                  <div key={mat.code || i} className="db-fav-card"
                    onClick={() => navigate(`/charts/material/${mat.code}`)}>
                    <div className="db-fav-name">{mat.name}</div>
                    <div className="db-fav-row">
                      <div>
                        <span className="db-fav-price">{mat.price || '—'}</span>
                      </div>
                      <div className="db-fav-sparkline">
                        <Sparkline data={values} up={up} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Widget Grid */}
        <div className="db-grid-container">
          <div
            className={`db-grid ${isEditMode ? 'edit-mode' : ''}`}
            ref={gridRef}
            style={isEditMode ? { gridTemplateRows: `repeat(${numRows}, ${ROW_H}px)` } : undefined}
            onDragOver={onGridDragOver}
            onDrop={onGridDrop}
          >
            {widgets.length === 0 && !isEditMode && (
              <div className="db-empty-state">
                <div className="db-empty-icon"><IconGridEmpty size={48}/></div>
                <div className="db-empty-text">Your dashboard is empty</div>
                <div className="db-empty-sub">Click "Edit Dashboard" to add widgets</div>
                <button className="db-btn db-btn-primary" onClick={enterEditMode} style={{ marginTop: 12 }}>
                  <IconPencil/> Edit Dashboard
                </button>
              </div>
            )}

            {/* Empty cell drop zones — rendered before widgets so they sit below */}
            {isEditMode && Array.from({ length: NUM_COLS * numRows }, (_, i) => {
              const col = i % NUM_COLS;
              const row = Math.floor(i / NUM_COLS);
              const isHighlighted = highlightedCells.has(`${col},${row}`);
              const cellOccupied = occupiedCells.has(`${col},${row}`);
              return (
                <div
                  key={`cell-${col}-${row}`}
                  className={[
                    'db-grid-cell',
                    cellOccupied ? 'occupied' : '',
                    isHighlighted ? 'can-drop' : '',
                  ].filter(Boolean).join(' ')}
                  style={{ gridColumn: col + 1, gridRow: row + 1 }}
                />
              );
            })}

            {widgets.map(widget => {
              const isDragging = draggedId === widget.id;
              const isDragOver = dragOverId === widget.id;
              return (
                <div
                  key={widget.id}
                  draggable={isEditMode}
                  onDragStart={e => onDragStart(e, widget.id)}
                  onDragEnd={onDragEnd}
                  onDragOver={e => onDragOver(e, widget.id)}
                  onDrop={e => onDrop(e, widget.id)}
                  className={[
                    'db-widget',
                    isEditMode ? 'edit-mode' : '',
                    isDragging ? 'dragging' : '',
                    isDragOver ? 'drag-over' : '',
                  ].filter(Boolean).join(' ')}
                  style={{
                    gridColumn: `${widget.col + 1} / span ${widget.colSpan}`,
                    gridRow: `${widget.row + 1} / span ${widget.rowSpan}`,
                  }}
                >
                  {isEditMode && (
                    <>
                      <div className="db-ctrl-drag">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
                          <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                          <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
                        </svg>
                      </div>
                      <button className="db-ctrl-delete" onClick={e => deleteWidget(e, widget.id)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                      <div
                        className="db-ctrl-resize"
                        onMouseDown={e => onResizeMouseDown(e, widget.id)}
                        title={`${widget.colSpan}×${widget.rowSpan} — drag to resize`}
                      >
                        <ResizeHandleIcon />
                        <span className="db-ctrl-resize-label">{widget.colSpan}×{widget.rowSpan}</span>
                      </div>
                    </>
                  )}
                  {renderWidget(widget)}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Add Widget Modal */}
      {showAddWidget && (
        <div className="db-modal-overlay" onClick={() => setShowAddWidget(false)}>
          <div className="db-modal" onClick={e => e.stopPropagation()}>
            <div className="db-modal-header">
              <span className="db-modal-title">
                {addWidgetType === 'chart-material' ? 'Select Material' : 'Add Widget'}
              </span>
              <button className="db-modal-close" onClick={() => setShowAddWidget(false)}><IconX size={12}/></button>
            </div>

            {!addWidgetType ? (
              <div className="db-widget-type-grid">
                {WIDGET_TYPES.map(wt => (
                  <button
                    key={wt.type}
                    className="db-type-btn"
                    onClick={() => {
                      if (wt.type === 'chart-material') {
                        setAddWidgetType('chart-material');
                      } else {
                        addWidget(wt.type);
                      }
                    }}
                  >
                    <div className="db-type-icon">{wt.icon}</div>
                    <div className="db-type-name">{wt.name}</div>
                    <div className="db-type-desc">{wt.desc}</div>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <input
                  className="db-search-input"
                  placeholder="Search materials…"
                  value={matSearch}
                  onChange={e => setMatSearch(e.target.value)}
                  autoFocus
                />
                <div className="db-material-list">
                  {materialsLoading && (
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, padding: 12 }}>Loading…</div>
                  )}
                  {(allMaterials || [])
                    .filter(m => !matSearch || m.name.toLowerCase().includes(matSearch.toLowerCase()) || m.code.toLowerCase().includes(matSearch.toLowerCase()))
                    .slice(0, 50)
                    .map(m => (
                      <div key={m.code} className="db-material-item"
                        onClick={() => addWidget('chart-material', { materialCode: m.code, color: '#8b5cf6' })}>
                        <div>
                          <div className="db-material-item-name">{m.name}</div>
                          <div className="db-material-item-code">{m.code}</div>
                        </div>
                        <div className="db-material-item-price">{m.price || '—'}</div>
                      </div>
                    ))
                  }
                </div>
                <div style={{ marginTop: 12 }}>
                  <button className="db-btn" onClick={() => setAddWidgetType(null)}><IconArrowLeft/> Back</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Tab Confirm */}
      {deleteTabConfirm && (
        <div className="db-confirm-overlay" onClick={() => setDeleteTabConfirm(null)}>
          <div className="db-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="db-confirm-title">Delete dashboard?</div>
            <div className="db-confirm-text">
              Delete "{dashboards.find(d => d.id === deleteTabConfirm)?.name}"?
              This cannot be undone.
            </div>
            <div className="db-confirm-actions">
              <button className="db-btn" onClick={() => setDeleteTabConfirm(null)}>Cancel</button>
              <button className="db-btn db-btn-danger" onClick={() => deleteTab(deleteTabConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Utility ──────────────────────────────────────────────────────────────────

function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return `${Math.floor(diff / 60000)}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default DashboardPage;
